"use client";

import * as RadixToast from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "./cn";

export interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  tone?: "success" | "error";
}

const ToastContext = createContext<((toast: Omit<ToastMessage, "id">) => void) | null>(null);

/**
 * Brief confirmation only — important errors stay next to the action that
 * caused them, never solely inside a toast. Not wired into any screen yet.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const notify = useCallback((toast: Omit<ToastMessage, "id">) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...toast, id }]);
  }, []);

  return (
    <ToastContext.Provider value={notify}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <RadixToast.Root
            key={toast.id}
            className={cn("ui-toast", toast.tone)}
            duration={4000}
            onOpenChange={(open) => {
              if (!open) setToasts((current) => current.filter((item) => item.id !== toast.id));
            }}
          >
            <RadixToast.Title>{toast.title}</RadixToast.Title>
            {toast.description ? (
              <RadixToast.Description>{toast.description}</RadixToast.Description>
            ) : null}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="ui-toast-viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const notify = useContext(ToastContext);
  if (!notify) throw new Error("useToast must be used within a ToastProvider");
  return notify;
}
